import { useSyncExternalStore } from 'react';

// Shared "install to Home Screen" state for the menu item and the one-time
// hint (see InstallHint).
//
// - iPhone/iPad Safari can't be prompted programmatically, so there we show
//   instructions (Share → Add to Home Screen).
// - Android Chrome fires `beforeinstallprompt` once the site is installable
//   (manifest.json needs 192px + 512px icons). We keep that event so the menu
//   item can open Chrome's native install dialog, but deliberately don't call
//   preventDefault() on it, which would suppress Chrome's own automatic
//   install banner.

const DISMISSED_KEY = 'landscapeInstallHintDismissed'; // kept from the landscape-only hint so existing dismissals stick
const VISIT_COUNT_KEY = 'atl-visit-count';
const VISIT_COUNTED_KEY = 'atl-visit-counted'; // sessionStorage: one count per browsing session

const safeGet = (storage, key) => { try { return storage.getItem(key); } catch { return null; } };
const safeSet = (storage, key, value) => { try { storage.setItem(key, value); } catch { /* private mode etc. */ } };

export const isStandalone = () => typeof window !== 'undefined' && (
    window.navigator.standalone === true
    || (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
);

// Safari proper on iPhone/iPad — not Chrome/Firefox/Edge/Opera on iOS, which
// can't add to the Home Screen the same way — and not already installed.
export const isIosSafariInBrowser = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    return /iPhone|iPad|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua) && !isStandalone();
};

// Count visits (browsing sessions), once per session, at module load.
if (typeof window !== 'undefined' && !safeGet(window.sessionStorage, VISIT_COUNTED_KEY)) {
    const count = parseInt(safeGet(window.localStorage, VISIT_COUNT_KEY) || '0', 10) || 0;
    safeSet(window.localStorage, VISIT_COUNT_KEY, String(count + 1));
    safeSet(window.sessionStorage, VISIT_COUNTED_KEY, 'true');
}

let state = {
    deferredPrompt: null, // Android: the saved beforeinstallprompt event
    hintOpenedFromMenu: false, // iOS: hint opened on request, even if previously dismissed
    hintDismissed: safeGet(typeof window !== 'undefined' ? window.localStorage : null, DISMISSED_KEY) === 'true',
    visitCount: parseInt(safeGet(typeof window !== 'undefined' ? window.localStorage : null, VISIT_COUNT_KEY) || '1', 10) || 1,
};
const listeners = new Set();
const setState = (patch) => {
    state = { ...state, ...patch };
    listeners.forEach((l) => l());
};

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => setState({ deferredPrompt: e }));
    window.addEventListener('appinstalled', () => setState({ deferredPrompt: null, hintOpenedFromMenu: false }));
}

const subscribe = (listener) => { listeners.add(listener); return () => listeners.delete(listener); };
const getSnapshot = () => state;

export const dismissInstallHint = () => {
    safeSet(window.localStorage, DISMISSED_KEY, 'true');
    setState({ hintDismissed: true, hintOpenedFromMenu: false });
};

// The menu item: Chrome's native dialog where available, otherwise (iOS
// Safari) the instructions hint.
export const requestInstall = async () => {
    const { deferredPrompt } = state;
    if (deferredPrompt) {
        try {
            await deferredPrompt.prompt();
            await deferredPrompt.userChoice;
        } catch { /* already used or unavailable — fall through */ }
        // A beforeinstallprompt event can only be prompted once.
        setState({ deferredPrompt: null });
        return;
    }
    if (isIosSafariInBrowser()) setState({ hintOpenedFromMenu: true });
};

// { canInstall, hintOpenedFromMenu, hintDismissed, visitCount }
export const useInstallPrompt = () => {
    const s = useSyncExternalStore(subscribe, getSnapshot);
    return {
        canInstall: !isStandalone() && (!!s.deferredPrompt || isIosSafariInBrowser()),
        hintOpenedFromMenu: s.hintOpenedFromMenu,
        hintDismissed: s.hintDismissed,
        visitCount: s.visitCount,
    };
};
